import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Spinner, Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Input, Tooltip } from "@heroui/react";
import { Typography, UniversalTable } from "../../../../../components";
import { FilterSelect } from "../../../../../components/ui";
import { staffApi, OnlyFansAccount } from "../../../../../api/staff";
import { useTranslations } from "../../../../../contexts/translation.context";
import { EmptyContentIc, ResetFiltersIc } from "../../../../../components/icons";
import {
  IdCell,
  AccountCell,
  CampaignsCell,
  OrdersCell,
  StatusCell
} from "../../../../accounts/onlyfans/components";
import { showPartnerSuccess, showError } from "../../../../../utils/toast";

const SEARCH_DEBOUNCE_DELAY = 500;

interface ModelsTabProps {
  creatorId: string;
}

interface ModelsFilters {
  search: string;
  status: string;
}

const DEFAULT_FILTERS: ModelsFilters = {
  search: '',
  status: 'all',
};

/**
 * ModelsTab Component
 *
 * Таб со списком OnlyFans аккаунтов (моделей) криейтора.
 * Использует UniversalTable для отображения данных с пагинацией.
 */
const ModelsTab: React.FC<ModelsTabProps> = ({ creatorId }) => {
  const { t } = useTranslations();
  const [models, setModels] = useState<OnlyFansAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<ModelsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState(filters.search);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Sync local search with filters
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // API params
  const apiParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      page_size: pageSize,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.status !== 'all') {
      params.status = filters.status;
    }

    return params;
  }, [currentPage, pageSize, filters]);

  const fetchModels = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await staffApi.getCreatorModels(creatorId, apiParams);
      // Map id to _id for compatibility with table components
      const modelsWithId = response.objects.map((model) => ({
        ...model,
        _id: model.id || model._id,
        id: model.id || model._id
      }));
      setModels(modelsWithId);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Error fetching models:", error);
      setModels([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [creatorId, apiParams]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleFilterChange = useCallback((key: keyof ModelsFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.status !== 'all';
  }, [filters]);

  // Define table columns matching OnlyFansAccountsTable
  const columns = [
    {
      key: "id",
      name: "ID",
      sortable: false
    },
    {
      key: "account",
      name: t('admin.onlyfans.columns.account'),
      sortable: false
    },
    {
      key: "campaigns",
      name: t('admin.onlyfans.columns.campaigns'),
      sortable: false
    },
    {
      key: "orders",
      name: t('admin.onlyfans.columns.orders'),
      sortable: false
    },
    {
      key: "status",
      name: t('admin.onlyfans.columns.status'),
      sortable: false
    },
    {
      key: "actions",
      name: "",
      sortable: false
    }
  ];

  // Handlers for actions
  const handleActivate = useCallback(async (accountId: string) => {
    try {
      await staffApi.activateOnlyFansAccount(accountId);
      showPartnerSuccess(t('admin.onlyfans.actions.activateSuccess'));
      fetchModels();
    } catch (error: any) {
      console.error('Error activating account:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Не удалось активировать аккаунт';
      showError(errorMessage);
    }
  }, [fetchModels, t]);

  const handleDeactivate = useCallback(async (accountId: string) => {
    try {
      await staffApi.deactivateOnlyFansAccount(accountId);
      showPartnerSuccess(t('admin.onlyfans.actions.deactivateSuccess'));
      fetchModels();
    } catch (error: any) {
      console.error('Error deactivating account:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Не удалось деактивировать аккаунт';
      showError(errorMessage);
    }
  }, [fetchModels, t]);

  const handleVerify = useCallback(async (accountId: string) => {
    try {
      await staffApi.verifyOnlyFansAccount(accountId);
      showPartnerSuccess(t('admin.onlyfans.actions.verifySuccess'));
      fetchModels();
    } catch (error: any) {
      console.error('Error verifying account:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Не удалось верифицировать аккаунт';
      showError(errorMessage);
    }
  }, [fetchModels, t]);

  const handleAddHeaders = useCallback(async (accountId: string, accountName: string) => {
    // TODO: Implement Add Headers modal when API is ready
    console.log('Add headers for account:', accountId, accountName);
    showError('Функция добавления headers будет доступна позже');
  }, []);

  // Cell renderer
  const renderCell = useCallback((account: OnlyFansAccount, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return <IdCell account={account} />;
      case "account":
        return <AccountCell account={account} />;
      case "campaigns":
        return <CampaignsCell account={account} />;
      case "orders":
        return <OrdersCell account={account} />;
      case "status":
        return <StatusCell account={account} />;
      case "actions":
        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                size="sm"
                variant="light"
                className="min-w-[32px] w-[32px] h-[32px] p-0"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 5.83333C10.9205 5.83333 11.6667 5.08714 11.6667 4.16667C11.6667 3.24619 10.9205 2.5 10 2.5C9.07953 2.5 8.33333 3.24619 8.33333 4.16667C8.33333 5.08714 9.07953 5.83333 10 5.83333Z" fill="#71717A"/>
                  <path d="M10 11.6667C10.9205 11.6667 11.6667 10.9205 11.6667 10C11.6667 9.07953 10.9205 8.33333 10 8.33333C9.07953 8.33333 8.33333 9.07953 8.33333 10C8.33333 10.9205 9.07953 11.6667 10 11.6667Z" fill="#71717A"/>
                  <path d="M10 17.5C10.9205 17.5 11.6667 16.7538 11.6667 15.8333C11.6667 14.9129 10.9205 14.1667 10 14.1667C9.07953 14.1667 8.33333 14.9129 8.33333 15.8333C8.33333 16.7538 9.07953 17.5 10 17.5Z" fill="#71717A"/>
                </svg>
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Account actions">
              {account.status === 'moderation' && (
                <DropdownItem
                  key="verify"
                  className="text-success"
                  color="success"
                  onPress={() => handleVerify(account._id)}
                >
                  {t('admin.onlyfans.actions.verify')}
                </DropdownItem>
              )}
              <DropdownItem
                key="addHeaders"
                onPress={() => handleAddHeaders(account._id, account.name)}
              >
                {t('admin.onlyfans.actions.addHeaders')}
              </DropdownItem>
              {account.status === 'deactivated' ? (
                <DropdownItem
                  key="activate"
                  className="text-success"
                  color="success"
                  onPress={() => handleActivate(account._id)}
                >
                  {t('admin.onlyfans.actions.activate')}
                </DropdownItem>
              ) : (
                <DropdownItem
                  key="deactivate"
                  className="text-danger"
                  color="danger"
                  onPress={() => handleDeactivate(account._id)}
                >
                  {t('admin.onlyfans.actions.deactivate')}
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        );
      default:
        return null;
    }
  }, [handleActivate, handleDeactivate, handleVerify, handleAddHeaders, t]);

  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc />
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {hasActiveFilters
              ? (t('admin.creators.details.models.empty.filteredTitle') || 'По выбранным фильтрам нет аккаунтов')
              : (t('admin.creators.details.models.empty.title') || 'Нет аккаунтов')
            }
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {hasActiveFilters
              ? (t('admin.creators.details.models.empty.filteredDescription') || 'Попробуйте изменить параметры фильтров')
              : (t('admin.creators.details.models.empty.description') || 'У этого криейтора пока нет аккаунтов')
            }
          </Typography>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (isLoading && models.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Status options
  const statusOptions = [
    { key: 'all', label: t('admin.onlyfans.filters.status.all') || 'Все статусы' },
    { key: 'active', label: t('admin.onlyfans.filters.status.active') || 'Активен' },
    { key: 'moderation', label: t('admin.onlyfans.filters.status.moderation') || 'На модерации' },
    { key: 'wait_media', label: t('admin.onlyfans.filters.status.waitMedia') || 'Ожидание медиа' },
    { key: 'deactivated', label: t('admin.onlyfans.filters.status.deactivated') || 'Деактивирован' },
  ];

  return (
    <div className="flex flex-col">
      {/* Filters Section */}
      <div className="pb-[24px] flex items-center gap-[16px] flex-wrap justify-between w-full">
        <div className="flex items-center gap-[16px] flex-wrap justify-start">
          {/* Search Input */}
          <Input
            type="text"
            placeholder={t('admin.onlyfans.filters.search.placeholder') || 'Поиск'}
            value={localSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalSearch(e.target.value)}
            endContent={
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17.5 17.5L13.875 13.875" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            classNames={{
              mainWrapper: "w-[273px]",
              base: "w-[273px]",
              input: "text-sm",
              inputWrapper: "rounded-lg h-[36px] w-[273px]",
            }}
          />

          {/* Status Filter */}
          <FilterSelect
            placeholder={t('admin.onlyfans.filters.status.placeholder') || 'Статус'}
            options={statusOptions}
            selectedKey={filters.status}
            variant="campaign"
            onSelectionChange={(value) => handleFilterChange('status', value)}
            startContent={
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 6.66667V10" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 13.3333H10.0083" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            autoWidth={true}
          />

          {/* Reset Filters Button */}
          {hasActiveFilters && (
            <Tooltip content={t('admin.onlyfans.filters.resetTooltip') || 'Сбросить фильтры'} placement="top">
              <Button
                size="sm"
                className="h-[36px] rounded-[8px] !w-[36px] p-0 bg-[#F4F4F5] min-w-[36px]"
                onPress={handleResetFilters}
              >
                <ResetFiltersIc />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1">
        <UniversalTable
          data={models}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          currentPage={currentPage}
          currentPageSize={pageSize}
        />
      </div>
    </div>
  );
};

export default ModelsTab;
